import { useEffect, useRef } from "react";

import { api } from "@/services/api";
import { StreamError, getEventStream } from "@/services/sse";

/**
 * Be told when the member on screen changes, and re-read it.
 *
 * The server holds a connection open and writes a frame naming a member
 * whenever anyone writes to one — another clerk at another till, an amendment,
 * the nightly sweep that expires a lapsed card. A frame says only *which*
 * member; the answer to it is to re-read that member through the endpoint that
 * already decides what this person may see. So `onChange` is the page's own
 * refresh, not a setter.
 *
 * Every screen gets every frame and discards what it is not showing. That is
 * cheaper than it sounds — a frame is forty bytes — and it means the server
 * never has to know who is looking at what.
 */

/** Back off 1s, 2s, 4s … to half a minute, so a server that is down is not hammered. */
const FIRST_RETRY_MS = 1_000;
const LONGEST_RETRY_MS = 30_000;

export function useMemberStream(
  memberId: number | string | undefined,
  onChange: () => void,
  { enabled = true }: { enabled?: boolean } = {},
): void {
  // Through a ref so that a caller passing a fresh arrow function every render
  // does not tear the connection down and build it again each time.
  const latest = useRef(onChange);
  useEffect(() => {
    latest.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!enabled || memberId == null) return;
    const wanted = String(memberId);

    const control = new AbortController();
    let retryIn = FIRST_RETRY_MS;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // Kept across reconnects: this is what turns a dropped connection into a
    // gap of nothing rather than a gap of missed changes.
    let lastEventId: string | null = null;
    let stopped = false;

    const connect = async () => {
      try {
        const frames = getEventStream(`${api.defaults.baseURL}/stream`, {
          signal: control.signal,
          lastEventId,
        });
        for await (const frame of frames) {
          // Arriving at all means the connection works, whatever it carries —
          // so the backoff resets here and not only on a member frame.
          retryIn = FIRST_RETRY_MS;
          // The id is the envelope's, not the payload's — it is Redis's entry
          // id, and it is what resuming asks for. Only the change frames carry
          // one; `ready` and `ping` do not, and the guard is what keeps a quiet
          // spell from erasing the position rather than holding it.
          if (frame.id) lastEventId = frame.id;
          if (frame.event !== "member_changed") continue;
          const body = JSON.parse(frame.data) as { member_id?: number };
          if (String(body.member_id) === wanted) latest.current();
        }
      } catch (error) {
        // An abort is us, on the way out; anything else is worth another go.
        if (control.signal.aborted) return;
        // A refused stream is not a broken one. 401 has already been retried
        // once inside the reader and a second means the session is gone; 403
        // means this person may not read members at all. Reconnecting either
        // would be a request a second, forever, that can never succeed.
        if (error instanceof StreamError && (error.status === 401 || error.status === 403)) {
          return;
        }
      }
      if (stopped || control.signal.aborted) return;
      timer = setTimeout(connect, retryIn);
      retryIn = Math.min(retryIn * 2, LONGEST_RETRY_MS);
    };

    void connect();

    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      control.abort();
    };
  }, [memberId, enabled]);
}
